import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  ArrowUpRight,
  ShieldCheck,
  Lightning,
  Sparkle,
  CheckCircle,
  Phone,
  IdentificationCard,
  Bank,
  PiggyBank,
  Train,
  AirplaneTakeoff,
  Bus,
  Fingerprint,
  Money,
  CreditCard,
  QrCode,
  Coins,
  HandCoins,
  Buildings,
  GooglePlayLogo,
  AppleLogo,
  Storefront,
  Wallet,
  ChartBar,
  UsersThree,
  Receipt,
  PaperPlaneTilt,
  Package as PackageIcon,
} from "@phosphor-icons/react";
import {
  COMPANY,
  ACTIVE_SERVICES,
  UPCOMING_SERVICES,
  STATS,
  HOW_IT_WORKS,
  APP_URLS,
} from "../data/site";

const iconMap = {
  Phone,
  Lightning,
  IdentificationCard,
  Bank,
  PiggyBank,
  Train,
  AirplaneTakeoff,
  Bus,
  Fingerprint,
  Money,
  CreditCard,
  QrCode,
  Coins,
  HandCoins,
  Buildings,
  ShieldCheck,
  Receipt,
  PaperPlaneTilt,
};

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.06, duration: 0.55, ease: [0.2, 0.7, 0.2, 1] },
  }),
};

// Split upcoming services into two rows for the marquee
const half = Math.ceil(UPCOMING_SERVICES.length / 2);
const rowA = UPCOMING_SERVICES.slice(0, half);
const rowB = UPCOMING_SERVICES.slice(half);

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* ── HERO (centered, no illustration) ──────────────────────── */}
      <section data-testid="home-hero" className="brand-hero relative overflow-hidden">
        <div aria-hidden="true" className="brand-hero-grid absolute inset-0" />

        <div className="relative max-w-6xl mx-auto px-5 sm:px-8 lg:px-10 pt-20 pb-20 lg:pt-28 lg:pb-24 text-center">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full brand-hero-badge border backdrop-blur text-xs font-semibold tracking-[0.18em] uppercase text-white mb-7"
          >
            <Sparkle size={14} weight="fill" className="text-sky-200" />
            Retailer-first fintech, built for Bharat
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="font-heading font-black text-white text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.98] max-w-4xl mx-auto"
          >
            One app. Every service your{" "}
            <span className="brand-hero-highlight">retail shop</span> needs.
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.6 }}
            className="mt-6 text-base sm:text-lg text-white/85 max-w-2xl mx-auto leading-relaxed"
          >
            JayesPay turns your shop into a digital service hub — recharges,
            bill payments, PAN card, NSDL Bank BC, recurring deposits and a lot
            more on the way. Earn commission on every transaction.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.18, duration: 0.6 }}
            className="mt-9 flex flex-col sm:flex-row gap-3 justify-center"
          >
            <a
              href={APP_URLS.register}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-register-cta"
              className="btn-shimmer group inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full brand-hero-primary text-sm font-bold transition-colors"
            >
              Register as Retailer
              <ArrowRight
                size={16}
                weight="bold"
                className="group-hover:translate-x-0.5 transition-transform"
              />
            </a>
            <Link
              to="/services"
              data-testid="hero-explore-services-cta"
              className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full brand-hero-secondary border text-sm font-semibold transition-colors"
            >
              Explore services
              <ArrowUpRight size={16} weight="bold" />
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25, duration: 0.5 }}
            className="mt-9 flex flex-wrap items-center justify-center gap-x-6 gap-y-3 text-sm text-white/85"
          >
            {[
              "100% Digital KYC",
              "Transparent Commission",
              "Fast Retailer Onboarding",
              "24x7 Customer Care",
            ].map((t) => (
              <div key={t} className="flex items-center gap-1.5">
                <CheckCircle size={16} weight="fill" className="text-sky-200" />
                <span className="font-medium">{t}</span>
              </div>
            ))}
          </motion.div>

          {/* Service preview chips floating below hero */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35, duration: 0.7 }}
            className="mt-16 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 max-w-5xl mx-auto"
          >
            {[
              { Icon: Phone, label: "Recharge", color: "text-brandNavy", bg: "bg-surfaceBlue" },
              { Icon: Lightning, label: "Bill Payments", color: "text-brandNavy", bg: "bg-surfaceBlue" },
              { Icon: IdentificationCard, label: "PAN Card", color: "text-brandNavy", bg: "bg-surfaceBlue" },
              { Icon: Bank, label: "NSDL Bank BC", color: "text-brandNavy", bg: "bg-surfaceBlue" },
              { Icon: PiggyBank, label: "Recurring Deposit", color: "text-brandNavy", bg: "bg-surfaceBlue" },
            ].map(({ Icon, label, color, bg }, i) => (
              <motion.div
                key={label}
                whileHover={{ y: -4 }}
                transition={{ delay: 0.45 + i * 0.05 }}
                className="bg-surfaceAlt border border-line rounded-2xl p-4 flex flex-col items-center gap-2 hover:border-brandBlue/40 hover:shadow-md transition-all"
              >
                <div className={`h-11 w-11 rounded-xl ${bg} flex items-center justify-center`}>
                  <Icon size={22} weight="duotone" className={color} />
                </div>
                <div className="text-xs font-bold text-ink900 text-center leading-tight">
                  {label}
                </div>
                <div className="text-[9px] uppercase tracking-[0.18em] font-bold text-infoText">
                  Live
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── STATS STRIP ────────────────────────────────────────────── */}
      <section className="relative border-y border-line bg-surfaceAlt">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-10 py-10 grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-8">
          {STATS.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ delay: i * 0.06 }}
              className="text-center md:text-left"
              data-testid={`stat-${i}`}
            >
              <div className="font-heading font-black text-4xl sm:text-5xl tracking-tighter text-ink900">
                {s.value}
              </div>
              <div className="mt-1 text-xs uppercase tracking-[0.18em] font-semibold text-ink500">
                {s.label}
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ── ACTIVE SERVICES (tight 3-col grid) ─────────────────────── */}
      <section
        data-testid="home-services-section"
        className="relative py-20 md:py-24"
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-6 mb-10">
            <div className="lg:col-span-7">
              <div className="text-xs uppercase tracking-[0.22em] font-semibold text-infoText mb-2">
                Live on JayesPay
              </div>
              <h2 className="font-heading font-extrabold text-ink900 text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05]">
                Five services live today. <br />
                <span className="text-ink500">Many more on the runway.</span>
              </h2>
            </div>
            <div className="lg:col-span-5 flex lg:items-end">
              <p className="text-ink700 leading-relaxed">
                Every service is built end-to-end inside the retailer app —
                bill-fetch, auto-detect operators, browsable plans and clear
                commission visibility.
              </p>
            </div>
          </div>

          {/* Tight uniform 3-col grid; 6th slot is "view all" */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {ACTIVE_SERVICES.map((svc, i) => {
              const Icon = iconMap[svc.icon] || Phone;
              return (
                <motion.div
                  key={svc.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ delay: i * 0.05 }}
                  className="group relative bg-surfaceAlt border border-line rounded-2xl p-6 hover:shadow-lg hover:-translate-y-1 hover:border-lineStrong transition-all duration-300 overflow-hidden flex flex-col"
                  data-testid={`home-service-${svc.id}`}
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-brandBlue/20 to-brandBlue/5 border border-brandBlue/20 flex items-center justify-center">
                      <Icon size={22} weight="duotone" className="text-infoText" />
                    </div>
                    <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-infoText bg-infoBg px-2 py-0.5 rounded-full">
                      {svc.badge}
                    </span>
                  </div>
                  <h3 className="font-heading font-extrabold text-ink900 text-xl tracking-tight mb-2">
                    {svc.title}
                  </h3>
                  <p className="text-sm text-ink700 leading-relaxed mb-4 flex-1">
                    {svc.description}
                  </p>
                  <Link
                    to="/services"
                    data-testid={`home-service-${svc.id}-link`}
                    className="inline-flex items-center gap-1.5 text-xs font-bold text-ink900 uppercase tracking-[0.15em] group-hover:gap-2.5 transition-all"
                  >
                    Learn more <ArrowUpRight size={14} weight="bold" />
                  </Link>
                </motion.div>
              );
            })}

            {/* View all card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ delay: ACTIVE_SERVICES.length * 0.05 }}
              className="bg-brandNavy text-surface rounded-2xl p-6 flex flex-col justify-between hover:bg-brandBlue transition-colors"
              data-testid="home-service-viewall"
            >
              <div>
                <div className="h-12 w-12 rounded-xl brand-icon-tile-dark flex items-center justify-center mb-4">
                  <Receipt size={22} weight="duotone" className="text-ink900" />
                </div>
                <h3 className="font-heading font-extrabold text-xl tracking-tight mb-2">
                  See full catalogue
                </h3>
                <p className="text-sm text-surface/70 leading-relaxed mb-4">
                  Detailed list of every live service and what&apos;s coming
                  next.
                </p>
              </div>
              <Link
                to="/services"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-brandOrange text-ink900 text-xs font-bold w-fit"
              >
                Browse all services <ArrowRight size={14} weight="bold" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── COMING SOON — TWO ROWS SLIDING ─────────────────────────── */}
      <section className="py-20 bg-ink900 text-surface overflow-hidden relative">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-10 mb-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <div className="text-xs uppercase tracking-[0.22em] font-semibold text-brandOrange mb-3">
                On the runway
              </div>
              <h2 className="font-heading font-extrabold text-3xl sm:text-4xl tracking-tight">
                More services. Same app.
              </h2>
            </div>
            <p className="text-sm text-surface/65 max-w-md">
              IRCTC, AePS, m-ATM, Digital Gold, Loans and partner-bank accounts
              — rolling out in your retailer app.
            </p>
          </div>
        </div>

        {/* Edge fades */}
        <div className="pointer-events-none absolute inset-y-0 left-0 w-24 sm:w-40 bg-gradient-to-r from-ink900 to-transparent z-10" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-24 sm:w-40 bg-gradient-to-l from-ink900 to-transparent z-10" />

        {/* Row A — left to right */}
        <MarqueeRow items={rowA} duration={32} direction="left" testId="marquee-row-a" />
        {/* Row B — slightly slower, left to right */}
        <div className="mt-4">
          <MarqueeRow items={rowB} duration={40} direction="left" testId="marquee-row-b" offset />
        </div>
      </section>

      {/* ── HOW IT WORKS ───────────────────────────────────────────── */}
      <section
        data-testid="home-how-it-works"
        className="relative py-24 md:py-32 bg-surface"
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-10">
          <div className="max-w-3xl mb-14">
            <div className="text-xs uppercase tracking-[0.22em] font-semibold text-infoText mb-3">
              From signup to first transaction
            </div>
            <h2 className="font-heading font-extrabold text-ink900 text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05]">
              Six steps. <span className="text-gradient-brand">Zero friction.</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {HOW_IT_WORKS.map((s, i) => (
              <motion.div
                key={s.step}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ delay: i * 0.05 }}
                className="group relative bg-surfaceAlt border border-line rounded-3xl p-7 hover:border-brandBlue/40 hover:shadow-lg transition-all"
                data-testid={`how-it-works-step-${s.step}`}
              >
                <div className="flex items-start justify-between mb-6">
                  <span className="font-heading font-black text-5xl text-ink900/10 tracking-tighter group-hover:text-brandBlue/40 transition-colors">
                    {s.step}
                  </span>
                  <div className="h-9 w-9 rounded-full brand-icon-tile flex items-center justify-center">
                    <ArrowRight size={16} weight="bold" className="text-ink900" />
                  </div>
                </div>
                <h3 className="font-heading font-bold text-ink900 text-xl tracking-tight mb-2">
                  {s.title}
                </h3>
                <p className="text-sm text-ink700 leading-relaxed">
                  {s.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── APP HIGHLIGHTS STRIP ───────────────────────────────────── */}
      <section className="py-24 md:py-32 bg-surfaceAlt border-y border-line">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <div className="text-xs uppercase tracking-[0.22em] font-semibold text-infoText mb-3">
              Built for retailers
            </div>
            <h2 className="font-heading font-extrabold text-ink900 text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05] mb-6">
              Everything you need to run a fintech counter.
            </h2>
            <p className="text-ink700 leading-relaxed mb-8">
              Powerful dashboard, smart wallet, deep reports, refer & earn,
              T-PIN, app lock, settings and support — all designed for the way
              Bharat&apos;s retailers actually work.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/features"
                data-testid="home-features-link"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-brandNavy text-surfaceAlt text-sm font-bold hover:bg-brandBlue transition-colors"
              >
                See all features
                <ArrowRight size={16} weight="bold" />
              </Link>
              <Link
                to="/packages"
                data-testid="home-packages-link"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-surface border border-line text-ink900 text-sm font-semibold hover:bg-line/40 transition-colors"
              >
                <PackageIcon size={16} weight="bold" />
                View packages
              </Link>
            </div>
          </div>

          <div className="lg:col-span-7 grid grid-cols-2 gap-4">
            {[
              { Icon: Storefront, label: "Smart Dashboard" },
              { Icon: Wallet, label: "Smart Wallet" },
              { Icon: ChartBar, label: "Ledger & Reports" },
              { Icon: UsersThree, label: "Refer & Earn" },
              { Icon: ShieldCheck, label: "T-PIN & App Lock" },
              { Icon: IdentificationCard, label: "ID Card & Certificate" },
            ].map(({ Icon, label }, i) => (
              <motion.div
                key={label}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="bg-surface rounded-2xl border border-line p-5 hover:bg-infoBg/40 transition-colors"
              >
                <Icon size={26} weight="duotone" className="text-infoText mb-3" />
                <div className="text-sm font-bold text-ink900">{label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── DOWNLOAD APP ───────────────────────────────────────────── */}
      <section
        data-testid="home-download-section"
        className="relative py-24 md:py-32 bg-gradient-to-br from-brandNavy via-brandNavy to-brandBlue overflow-hidden"
      >
        <div className="absolute -top-32 left-1/3 h-72 w-72 rounded-full bg-brandBlue/40 blur-3xl" />
        <div className="absolute bottom-0 -right-20 h-80 w-80 rounded-full bg-brandOrange/20 blur-3xl" />

        <div className="relative max-w-5xl mx-auto px-5 sm:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-white/20 bg-white/10 text-xs font-semibold tracking-[0.18em] uppercase text-white mb-6">
            <Sparkle size={14} weight="fill" className="text-sky-200" />
            Mobile app · Android & iOS
          </div>
          <h2 className="font-heading font-black text-white text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.0] mb-5">
            Run your shop from your <span className="text-orange-300">pocket</span>.
          </h2>
          <p className="text-white/80 max-w-2xl mx-auto leading-relaxed mb-10">
            The JayesPay retailer app is launching soon on Play Store and App
            Store. Until then, access everything from the web app.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            {[
              { Icon: GooglePlayLogo, label: "Google Play", sub: "Coming Soon" },
              { Icon: AppleLogo, label: "App Store", sub: "Coming Soon" },
            ].map(({ Icon, label, sub }) => (
              <button
                key={label}
                type="button"
                data-testid={`download-${label.toLowerCase().replace(" ", "-")}-badge`}
                className="group inline-flex items-center gap-3 px-6 py-3.5 rounded-2xl border border-white/20 bg-white/10 text-white hover:bg-white/20 transition-colors cursor-default"
              >
                <Icon size={28} weight="fill" />
                <div className="text-left">
                  <div className="text-[10px] uppercase tracking-wider text-white/70 font-semibold">
                    {sub}
                  </div>
                  <div className="text-sm font-bold tracking-tight">
                    Get it on {label}
                  </div>
                </div>
              </button>
            ))}
          </div>

          <div className="mt-8 text-sm text-white/75">
            Or use the{" "}
            <a
              href={APP_URLS.login}
              target="_blank"
              rel="noopener noreferrer"
              className="text-white font-semibold underline-offset-2 hover:underline"
            >
              JayesPay Web App
            </a>{" "}
            available now.
          </div>
        </div>
      </section>

      {/* ── CTA BANNER ─────────────────────────────────────────────── */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <div className="relative overflow-hidden rounded-[2.5rem] bg-ink900 text-surface p-10 sm:p-16">
            <div className="absolute -top-24 -right-20 h-80 w-80 rounded-full bg-brandBlue/30 blur-3xl" />
            <div className="absolute -bottom-24 -left-20 h-72 w-72 rounded-full bg-brandOrange/20 blur-3xl" />
            <div className="relative grid lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-8">
                <div className="text-xs uppercase tracking-[0.22em] font-semibold text-brandOrange mb-3">
                  Become a JayesPay Partner
                </div>
                <h2 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05] mb-4">
                  Turn your shop into a digital service counter today.
                </h2>
                <p className="text-surface/75 max-w-xl">
                  Onboard digitally, complete KYC, activate a package — and
                  start serving customers in minutes.
                </p>
              </div>
              <div className="lg:col-span-4 flex lg:justify-end">
                <Link
                  to="/become-partner"
                  data-testid="cta-banner-become-partner"
                  className="btn-shimmer inline-flex items-center gap-2 px-7 py-4 rounded-full bg-brandOrange text-ink900 text-sm font-bold hover:bg-brandBlue hover:text-surfaceAlt transition-colors"
                >
                  Apply now
                  <ArrowRight size={16} weight="bold" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

/* ─── Marquee Row ────────────────────────────────────────────────── */
function MarqueeRow({ items, duration = 35, direction = "left", testId, offset = false }) {
  const Icon = ({ name }) => {
    const I = iconMap[name] || Sparkle;
    return <I size={20} weight="duotone" className="text-sky-200" />;
  };

  // Duplicate the items so the marquee can loop seamlessly
  const looped = [...items, ...items, ...items];

  return (
    <div className="relative" data-testid={testId}>
      <motion.div
        initial={{ x: offset ? "-12%" : 0 }}
        animate={{ x: direction === "left" ? "-50%" : "0%" }}
        transition={{
          duration,
          repeat: Infinity,
          ease: "linear",
          repeatType: "loop",
        }}
        className="flex gap-4 w-max"
      >
        {looped.map((s, i) => (
          <div
            key={`${s.title}-${i}`}
            className="flex items-center gap-3 px-6 py-4 rounded-full bg-surface/5 border border-surface/15 backdrop-blur whitespace-nowrap"
          >
            <Icon name={s.icon} />
            <span className="text-sm font-semibold">{s.title}</span>
          </div>
        ))}
      </motion.div>
    </div>
  );
}
